import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { KPICard } from '../components/KPICard';
import { getInterviews, getInterviewStats, getOverdueInterviews, scheduleInterview, completeInterview, sendInterviewReminder, generateInterviewMessage, sendCustomerSMS, sendCustomerEmail, getInterviewCalendar } from '../lib/api';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import {
  Phone,
  Calendar as CalendarIcon,
  Clock,
  CheckCircle,
  AlertTriangle,
  Send,
  MessageSquare,
  Loader2,
  ChevronRight,
  AlertCircle,
  Mail,
  Sparkles,
} from 'lucide-react';

export const InterviewConciergePage = () => {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [schedulingDeal, setSchedulingDeal] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [messageModal, setMessageModal] = useState(null);
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [generatedMessageType, setGeneratedMessageType] = useState('');
  const [messageLoading, setMessageLoading] = useState(false);
  const [sendingSMS, setSendingSMS] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [calendarDates, setCalendarDates] = useState([]);
  const [miniCalMonth, setMiniCalMonth] = useState(new Date());

  useEffect(() => {
    loadData();
  }, [filter]);

  // Load mini calendar data
  useEffect(() => {
    const m = miniCalMonth.getMonth() + 1;
    const y = miniCalMonth.getFullYear();
    getInterviewCalendar({ month: m, year: y }).then(res => {
      const dates = Object.keys(res.data?.by_date || {}).map(d => new Date(d + 'T12:00:00'));
      setCalendarDates(dates);
    }).catch(() => {});
  }, [miniCalMonth]);

  const loadData = async () => {
    try {
      let interviewsData;
      
      if (filter === 'overdue') {
        const res = await getOverdueInterviews();
        interviewsData = res.data;
      } else {
        const params = {};
        if (filter !== 'all') {
          params.status = filter;
        }
        const res = await getInterviews(params);
        interviewsData = res.data;
      }
      
      const statsRes = await getInterviewStats();
      
      setInterviews(interviewsData);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async (dealId) => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }
    
    try {
      await scheduleInterview({ deal_id: dealId, scheduled_time: selectedDate.toISOString() });
      toast.success('Interview scheduled');
      setSchedulingDeal(null);
      setSelectedDate(null);
      loadData();
    } catch (err) {
      toast.error('Failed to schedule');
    }
  };

  const handleComplete = async (dealId) => {
    try {
      await completeInterview(dealId);
      toast.success('Interview marked as completed');
      loadData();
    } catch (err) {
      toast.error('Failed to complete');
    }
  };

  const handleSendReminder = async (dealId) => {
    setSendingReminder(dealId);
    try {
      const res = await sendInterviewReminder(dealId);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleGenerateMessage = async (dealId, type) => {
    setMessageLoading(true);
    try {
      const res = await generateInterviewMessage(dealId, type);
      setGeneratedMessage(res.data.message);
      setGeneratedMessageType(type);
    } catch (err) {
      toast.error('Failed to generate message');
    } finally {
      setMessageLoading(false);
    }
  };

  const handleSendSMS = async () => {
    if (!generatedMessage || !messageModal) return;
    setSendingSMS(true);
    try {
      const res = await sendCustomerSMS(messageModal.id, generatedMessage, generatedMessageType);
      if (res.data.success) {
        toast.success(res.data.message);
        loadData();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to send SMS');
    } finally {
      setSendingSMS(false);
    }
  };

  const handleSendEmail = async () => {
    if (!generatedMessage || !messageModal) return;
    setSendingEmail(true);
    try {
      const res = await sendCustomerEmail(messageModal.id, generatedMessage, generatedMessageType);
      if (res.data.success) {
        toast.success(res.data.message);
        loadData();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const getStatusBadge = (interview) => {
    const status = interview.interview_status;
    const isOverdue = interview.interview_is_overdue;
    
    if (isOverdue) {
      return { class: 'bg-red-100 text-red-700 border border-red-300', label: 'OVERDUE' };
    }
    
    const styles = {
      not_started: { class: 'bg-slate-100 text-slate-700', label: 'Not Started' },
      scheduled: { class: 'bg-amber-100 text-amber-700', label: 'Scheduled' },
      completed: { class: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
    };
    return styles[status] || styles.not_started;
  };

  const getEscalationBadge = (level) => {
    if (!level || level === 0) return null;
    
    const badges = {
      1: { class: 'bg-amber-100 text-amber-700', label: 'L1' },
      2: { class: 'bg-red-100 text-red-700', label: 'L2' },
      3: { class: 'bg-red-200 text-red-800', label: 'CRITICAL' },
    };
    return badges[level] || null;
  };

  const isRowOverdue = (interview) => {
    return interview.interview_is_overdue || interview.interview_escalation_level >= 2;
  };

  return (
    <Layout>
      <Header
        title="Interview Concierge"
        subtitle="AI-powered interview management"
        onRefresh={loadData}
      />

      <div className="p-6 space-y-6" data-testid="interview-concierge">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <KPICard
            label="Requiring Interview"
            value={stats?.total_requiring_interview || 0}
            icon={Phone}
            type="default"
          />
          <KPICard
            label="Completed"
            value={stats?.completed || 0}
            icon={CheckCircle}
            type="success"
          />
          <KPICard
            label="Scheduled"
            value={stats?.scheduled || 0}
            icon={CalendarIcon}
            type="warning"
          />
          <KPICard
            label="Overdue"
            value={stats?.overdue || 0}
            icon={AlertTriangle}
            type="urgent"
          />
          <KPICard
            label="Escalated"
            value={(stats?.escalated_level_1 || 0) + (stats?.escalated_level_2 || 0)}
            icon={AlertCircle}
            type={stats?.escalated_level_2 > 0 ? 'urgent' : 'warning'}
          />
        </div>

        {/* Mini Calendar + View Full Calendar */}
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <CalendarIcon size={18} className="text-blue-600" />
              Interview Schedule
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/interviews/calendar')}
              className="text-xs"
              data-testid="open-full-calendar"
            >
              Open Full Calendar <ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
          <div className="flex items-start gap-6 flex-wrap">
            <Calendar
              mode="multiple"
              selected={calendarDates}
              onMonthChange={setMiniCalMonth}
              className="rounded border border-slate-200"
              modifiers={{ interview: calendarDates }}
              modifiersStyles={{
                interview: { backgroundColor: '#3b82f6', color: 'white', borderRadius: '50%' }
              }}
              data-testid="mini-calendar"
            />
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm text-slate-500 mb-3">Upcoming interviews this month</p>
              <div className="space-y-2">
                {calendarDates.length === 0 && (
                  <p className="text-xs text-slate-400 italic">No interviews scheduled this month</p>
                )}
                {calendarDates.slice(0, 5).map((date, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span className="text-slate-700 font-medium">
                      {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
                {calendarDates.length > 5 && (
                  <p className="text-xs text-slate-500">+{calendarDates.length - 5} more dates</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Warning Banner for Overdue */}
        {stats?.overdue > 0 && (
          <div className="bg-red-50 border-2 border-red-300 p-4 flex items-center gap-3" data-testid="overdue-banner">
            <AlertTriangle className="text-red-600" size={24} />
            <div>
              <p className="font-bold text-red-800">
                {stats.overdue} Interview{stats.overdue > 1 ? 's' : ''} Overdue - Immediate Action Required
              </p>
              <p className="text-sm text-red-600">
                {stats.escalated_level_2 > 0 && `${stats.escalated_level_2} escalated to Finance Director. `}
                Deals are stalling. Review and take action now.
              </p>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="bg-white border border-slate-200 p-1 flex gap-1 flex-wrap">
          {[
            { value: 'all', label: 'All' },
            { value: 'not_started', label: 'Not Started' },
            { value: 'scheduled', label: 'Scheduled' },
            { value: 'completed', label: 'Completed' },
            { value: 'overdue', label: 'Overdue', highlight: stats?.overdue > 0 },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${
                filter === tab.value
                  ? tab.highlight 
                    ? 'bg-red-600 text-white'
                    : 'bg-slate-900 text-white'
                  : tab.highlight
                    ? 'text-red-600 hover:bg-red-50 border border-red-200'
                    : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`filter-${tab.value}`}
            >
              {tab.label}
              {tab.highlight && tab.value === 'overdue' && (
                <span className="ml-1">({stats?.overdue})</span>
              )}
            </button>
          ))}
        </div>

        {/* Interview Pipeline */}
        {loading ? (
          <div className="bg-white border border-slate-200 p-8 text-center">
            <Loader2 className="animate-spin mx-auto text-slate-400 mb-2" size={32} />
            <p className="text-slate-500">Loading interviews...</p>
          </div>
        ) : interviews.length === 0 ? (
          <div className="bg-white border border-slate-200 p-8 text-center">
            <Phone size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No interviews found</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Lender</th>
                  <th>Status</th>
                  <th>Escalation</th>
                  <th>Scheduled</th>
                  <th>Reminders</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {interviews.map((interview) => {
                  const badge = getStatusBadge(interview);
                  const escalationBadge = getEscalationBadge(interview.interview_escalation_level);
                  const rowOverdue = isRowOverdue(interview);
                  
                  return (
                    <tr 
                      key={interview.id} 
                      className={`deal-row ${rowOverdue ? 'bg-red-50 hover:bg-red-100' : ''}`}
                      data-testid={`interview-row-${interview.id}`}
                    >
                      <td>
                        <div>
                          <p className="font-medium text-slate-900">{interview.customer_name}</p>
                          <p className="text-xs text-slate-500">#{interview.deal_number}</p>
                          {interview.interview_overdue_reason && (
                            <p className="text-xs text-red-600 mt-1">{interview.interview_overdue_reason}</p>
                          )}
                        </div>
                      </td>
                      <td className="text-sm">{interview.lender}</td>
                      <td>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold ${badge.class}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td>
                        {escalationBadge ? (
                          <span className={`inline-flex px-2 py-1 text-xs font-bold ${escalationBadge.class}`}>
                            {escalationBadge.label}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="font-mono text-sm">
                        {interview.interview_scheduled_time
                          ? format(new Date(interview.interview_scheduled_time), 'MM/dd/yyyy h:mm a')
                          : '-'}
                      </td>
                      <td className="font-mono text-sm">
                        <span className={interview.interview_reminders_sent > 2 ? 'text-red-600 font-bold' : ''}>
                          {interview.interview_reminders_sent}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {interview.interview_status === 'not_started' && (
                            <Popover
                              open={schedulingDeal === interview.id}
                              onOpenChange={(open) => {
                                setSchedulingDeal(open ? interview.id : null);
                                if (!open) setSelectedDate(null);
                              }}
                            >
                              <PopoverTrigger asChild>
                                <Button size="sm" variant="outline" data-testid={`schedule-btn-${interview.id}`}>
                                  <CalendarIcon size={14} className="mr-1" />
                                  Schedule
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="end">
                                <Calendar
                                  mode="single"
                                  selected={selectedDate}
                                  onSelect={setSelectedDate}
                                  initialFocus
                                />
                                <div className="p-3 border-t border-slate-100">
                                  <Button
                                    size="sm"
                                    className="w-full"
                                    onClick={() => handleSchedule(interview.id)}
                                    disabled={!selectedDate}
                                  >
                                    Confirm Schedule
                                  </Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                          
                          {interview.interview_status !== 'completed' && (
                            <Button
                              size="sm"
                              variant={rowOverdue ? "destructive" : "outline"}
                              onClick={() => handleSendReminder(interview.id)}
                              disabled={sendingReminder === interview.id}
                              data-testid={`reminder-btn-${interview.id}`}
                            >
                              {sendingReminder === interview.id ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <>
                                  <Send size={14} className="mr-1" />
                                  Remind
                                </>
                              )}
                            </Button>
                          )}
                          
                          {interview.interview_status === 'scheduled' && (
                            <Button
                              size="sm"
                              onClick={() => handleComplete(interview.id)}
                              data-testid={`complete-btn-${interview.id}`}
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Complete
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setMessageModal(interview)}
                            data-testid={`message-btn-${interview.id}`}
                            title="AI Message Generator"
                          >
                            <MessageSquare size={14} />
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/deal/${interview.id}`)}
                            title="View Deal"
                          >
                            <ChevronRight size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* AI Message Modal */}
        {messageModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" data-testid="message-modal">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="text-amber-500" size={24} />
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-900">
                  AI Interview Concierge
                </h3>
              </div>
              
              <div className="mb-4 p-3 bg-slate-50 border border-slate-200">
                <p className="text-sm">
                  <span className="font-semibold">{messageModal.customer_name}</span>
                  <span className="text-slate-500"> | {messageModal.lender}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Phone: {messageModal.customer_phone || 'Not provided'} | 
                  Email: {messageModal.customer_email || 'Not provided'}
                </p>
              </div>
              
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Generate Message</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleGenerateMessage(messageModal.id, 'intro')}
                    disabled={messageLoading}
                    className="justify-start"
                  >
                    {messageLoading && generatedMessageType === 'intro' ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <Mail size={14} className="mr-2" />
                    )}
                    Intro Message
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleGenerateMessage(messageModal.id, 'reminder')}
                    disabled={messageLoading}
                    className="justify-start"
                  >
                    {messageLoading && generatedMessageType === 'reminder' ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <Send size={14} className="mr-2" />
                    )}
                    Reminder
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleGenerateMessage(messageModal.id, 'confirmation')}
                    disabled={messageLoading}
                    className="justify-start"
                  >
                    {messageLoading && generatedMessageType === 'confirmation' ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <CheckCircle size={14} className="mr-2" />
                    )}
                    Confirmation
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleGenerateMessage(messageModal.id, 'interview_prep')}
                    disabled={messageLoading}
                    className="justify-start"
                  >
                    {messageLoading && generatedMessageType === 'interview_prep' ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <Sparkles size={14} className="mr-2" />
                    )}
                    Interview Prep
                  </Button>
                </div>
              </div>

              {generatedMessage && (
                <div className="mt-4 p-4 bg-slate-50 border border-slate-200">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Generated Content
                  </p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{generatedMessage}</p>
                  
                  {/* Send Buttons */}
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                      Send to Customer
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleSendSMS}
                        disabled={sendingSMS || !messageModal.customer_phone}
                        className="flex-1"
                      >
                        {sendingSMS ? (
                          <Loader2 size={14} className="mr-1 animate-spin" />
                        ) : (
                          <MessageSquare size={14} className="mr-1" />
                        )}
                        Send SMS
                        {!messageModal.customer_phone && <span className="ml-1 text-xs text-amber-600">(No phone)</span>}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleSendEmail}
                        disabled={sendingEmail || !messageModal.customer_email}
                        className="flex-1"
                      >
                        {sendingEmail ? (
                          <Loader2 size={14} className="mr-1 animate-spin" />
                        ) : (
                          <Mail size={14} className="mr-1" />
                        )}
                        Send Email
                        {!messageModal.customer_email && <span className="ml-1 text-xs text-amber-600">(No email)</span>}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setMessageModal(null);
                    setGeneratedMessage('');
                    setGeneratedMessageType('');
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
